// src/pages/usa/colorado/Mosca.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

// ⭐ STRICT COMMAND: Mosca ALWAYS goes back to Colorado map
export const backRoute = "/map/colorado";

export default function Mosca() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly (NO LOOPS)
  useEffect(() => {
    if (!window.__moscaBackRouteSet) {
      window.__moscaBackRouteSet = true;
      navigate(".", { replace: true, state: { backRoute } });
    }
  }, [navigate]);

  return (

    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Mosca, Colorado</h1>

        <section className="travel-review">
          {/* LEFT COLUMN — TEXT */}
          <div className="travel-text">
            <h2>🏞️ Discover Mosca, Colorado</h2>

            <p>
              Mosca, Colorado sits quietly in the San Luis Valley at an elevation of 7,559 feet, surrounded by the dramatic rise of the Sangre de Cristo Mountains. Just minutes from Great Sand Dunes National Park and the icy cascade of Zapata Falls, Mosca offers a rugged, high-desert vibe perfect for solitude, stargazing, and unexpected roadside quirks — like the Colorado Gators Reptile Park. It’s the kind of place where the silence feels earned, and every trailhead hints at something epic.
            </p>

            {/* GETTING THERE */}
            <h3>🚗 Getting There</h3>

            <p>
              If you're planning to visit Mosca, there are a few ways to get there depending on your style. Driving in from Colorado Springs takes just under 3 hours via I‑25 and US‑160, with wide‑open views and quirky stops along the way. From Denver, it's closer to 4 hours — a scenic descent into the San Luis Valley.
            </p>

            <p>
              Flying? The nearest airport is Alamosa (ALS), just 18 minutes away, though most travelers opt for Colorado Springs (COS) or Durango (DRO) for better connections. From there, it’s a mix of bus routes and rental car legs, but the payoff is real: Mosca’s quiet, high‑desert welcome is worth every mile.
            </p>

            {/* PERSONAL OBSERVATION */}
            <h3>🌟 My Personal Observation</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Great Sand Dunes:</strong> Colorado isn’t just mountains — it’s also home to the surreal Great Sand Dunes National Park. I’ve hiked to the top, rolled in the sand like a kid, and sprinted downhill just for the thrill. The <em>Great Sand Dunes Oasis</em> rents sandboards for sliding down the dunes.
                  <br />
                  One time I was halfway to the dunes and got caught in a full‑blown sandstorm — wild, gritty, unforgettable.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=37.75557514888103,-105.53718593451269"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Great Sand Dunes
                  </a>
                  <br />
                  <strong>The Great Sand Dunes Oasis Restaurant:</strong>{" "}
                  <a
                    href="https://www.google.com/maps?q=37.69249086213359,-105.54789587663326"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Great Sand Dunes Oasis Restaurant
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Zapata Falls:</strong> Just outside the Sand Dunes, tucked into the mountains, lies this hidden waterfall. It’s the perfect refresh after hiking over warm sand — soaking your feet and splashing cold water on your face feels incredible. The falls are concealed inside a narrow canyon, making the reveal even more dramatic.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=37.62160351336904,-105.55966729201255"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Zapata Falls
                  </a>
                </div>
              </li>
            </ul>

            {/* HIDDEN GEMS */}
            <h3>⛰️ Hidden Gems in Mosca</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Colorado Gators Reptile Park:</strong> A desert oddity where rescued gators, tortoises, and snakes thrive in geothermal water. Hold a baby gator, tour the tilapia farm, or just marvel that this place exists in Mosca.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>UFO Watchtower:</strong> A quirky roadside stop near Hooper with an observation deck, healing garden, gift shop, and campground. Admission: $5 adults, $15 overnight camping (subject to change).
                  <br />
                  <a
                    href="https://www.colorado.com/alamosa/ufo-watchtower"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Colorado.com listing
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Sand Dunes Recreation:</strong> A family‑friendly hot spring in Hooper with pools, therapy tubs, and an adults‑only Greenhouse. Perfect after a day in the dunes.
                </div>
              </li>
            </ul>

            {/* PLACES TO STAY */}
            <h3>🛏️ Places to Stay Near Mosca</h3>

            <p>
              I haven’t stayed overnight in Mosca myself — I usually drive back to Denver or Colorado Springs — but these are consistently top‑ranked by TripAdvisor, Expedia, and Google Reviews:
            </p>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Rustic Rook Resort:</strong> Glamping tents with mountain views, breakfast included, and close access to the dunes and Zapata Falls.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Great Sand Dunes Lodge:</strong> Simple lodge with unbeatable dune views and patio seating.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Ramble at Great Sand Dunes:</strong> Premium campground with wind‑protected sites and hot showers.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Sand Dunes Recreation RV Park:</strong> On‑site hot springs, full hookups, and access to pools.
                </div>
              </li>
            </ul>

            {/* AFFILIATE BLOCK */}
            <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
                        <div className="affiliate-block">
                          <AffiliateExpedia />
                          <AffiliateBooking />
                          <AffiliateTrip />
                        </div>

            {/* VIDEOS */}
            <h3>🎥 My Travel Videos</h3>

            <p>Here are some videos I took from Sand Dunes and Zapata Falls:</p>

            <p>
              <a
                href="https://youtu.be/WQ8PyDOhWqo?si=usvxJSeUClnvmyML"
                target="_blank"
                rel="noopener noreferrer"
              >
                Zapata Falls
              </a>
              <br />
              <a
                href="https://youtu.be/pcwcR2zDaZY?si=kOLM6Jg9QEWjOhrX"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sand Dunes 360 View
              </a>
              <br />
              <a
                href="https://youtu.be/7oGokNUFQUs?si=dItHW0aFhgEcESA8"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sand Dunes
              </a>
              <br />
              <a
                href="https://youtu.be/1qfDiQ8gzw8?si=YeMPP0qY9ufcku-5"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sand Storm
              </a>
            </p>
          </div>

          {/* RIGHT COLUMN — IMAGES */}
          <div className="travel-image-column">
            <a
              href="https://youtu.be/7oGokNUFQUs?si=dItHW0aFhgEcESA8"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/mosca/hikingdunes.jpg"
                alt="Sand Dunes Video"
              />
            </a>

            <a
              href="https://youtu.be/z-wQA3Jw5AU?si=RlpPK5j_UmHKsGx0"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/mosca/inside.jpg"
                alt="Zapata Falls Video"
              />
            </a>

            <img src="/images/usa/colorado/mosca/restforthehike.jpg" alt="Resting during hike" />
            <img src="/images/usa/colorado/mosca/sanddune5.jpg" alt="Sand Dunes" />
            <img src="/images/usa/colorado/mosca/sd3.jpg" alt="Sand Dunes" />
            <img src="/images/usa/colorado/mosca/sanddunessign.jpg" alt="Sand Dunes Entrance Sign" />
            <img src="/images/usa/colorado/mosca/sanddunesdistance.jpg" alt="Sand Dunes from distance" />
            <img src="/images/usa/colorado/mosca/sanddunefalls.jpg" alt="Sand Dune Falls" />
            <img src="/images/usa/colorado/mosca/sign.jpg" alt="Zapata Falls Sign" />
            <img src="/images/usa/colorado/mosca/zz.jpg" alt="Zapata Falls Outside" />
          </div>
        </section>
      </div>

      {/* EMAIL BUTTON */}
      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      {/* POPUP */}
      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
